import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import powerSound from "./assets/sounds/power_on.mp3";
import logoVideo from "./assets/video/logo.mp4";

/**
 * mode: "on" | "off"
 */
export default function SplashScreen({ onFinish, mode = "on" }) {
  const { t } = useTranslation();
  const [fadeOut, setFadeOut] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [glow, setGlow] = useState(false);
  const [bootText, setBootText] = useState("");
  const [scanlines, setScanlines] = useState(true);
  const [particlePositions, setParticlePositions] = useState([]);

  const isPoweringOn = mode === "on";
  const mainColor = isPoweringOn ? "#22c55e" : "#dc2626"; // brighter green / red
  const glowColor = isPoweringOn
    ? "rgba(34,197,94,0.7)"
    : "rgba(220,38,38,0.7)";
  const shadowColor = isPoweringOn
    ? "rgba(34,197,94,0.3)"
    : "rgba(220,38,38,0.3)";

  useEffect(() => {
    // Generate particle positions for ambient animation
    const particles = Array.from({ length: 6 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2,
    }));
    setParticlePositions(particles);

    const audio = new Audio(powerSound);
    audio.volume = 0.4;
    audio.play().catch(() => {});

    const textTimer = setTimeout(() => setTextVisible(true), 300);
    const glowTimer = setTimeout(() => setGlow(true), 600);
    const scanlineTimer = setTimeout(() => setScanlines(false), 2800);
    const fadeTimer = setTimeout(() => setFadeOut(true), 3200);
    const endTimer = setTimeout(() => onFinish?.(), 4000);

    // Typewriter sequence
    const msg = isPoweringOn ? t('splash.initializing') : t('splash.poweringDown');
    let i = 0;
    const typeTimer = setInterval(() => {
      setBootText(msg.slice(0, i + 1));
      i++;
      if (i >= msg.length) clearInterval(typeTimer);
    }, 60);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(glowTimer);
      clearTimeout(scanlineTimer);
      clearTimeout(fadeTimer);
      clearTimeout(endTimer);
      clearInterval(typeTimer);
    };
  }, [onFinish, isPoweringOn]);

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes scanlines {
          0% { opacity: 0.8; }
          50% { opacity: 0.6; }
          100% { opacity: 0.8; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>
      <div
        className={`flex flex-col items-center justify-center h-screen w-full overflow-hidden relative transition-all duration-1000 ${
          fadeOut ? "opacity-0 scale-105" : "opacity-100 scale-100"
        }`}
        style={{
          background: isPoweringOn
            ? "radial-gradient(circle at center, #f0fdf4 0%, #dcfce7 30%, #bbf7d0 60%, #ffffff 100%)"
            : "radial-gradient(circle at center, #fef2f2 0%, #fee2e2 30%, #fecaca 60%, #ffffff 100%)",
        }}
      >
      {/* Animated background particles */}
      {particlePositions.map((particle, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: "4px",
            height: "4px",
            background: mainColor,
            opacity: 0.2,
            animation: `float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
            boxShadow: `0 0 8px ${mainColor}40`,
          }}
        />
      ))}

      {/* Device frame */}
      <div
        className={`relative w-[440px] h-[600px] sm:w-[520px] sm:h-[680px] bg-black rounded-[40px] border-[10px] border-gray-900 overflow-hidden transition-all duration-[1800ms] shadow-2xl ${
          glow ? "animate-pulse-slow" : ""
        }`}
        style={{
          boxShadow: glow
            ? `0 0 120px ${glowColor}, 0 0 60px ${glowColor} inset, 0 20px 60px rgba(0,0,0,0.6)`
            : `0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)`,
        }}
      >
        {/* Camera dot + speakers */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex gap-2 items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-gray-800" />
          <div className="w-3.5 h-1 rounded-full bg-gray-700/80" />
          <div className="w-3.5 h-1 rounded-full bg-gray-700/80" />
          <div className="w-2 h-2 rounded-full bg-gray-800" />
        </div>

        {/* Screen */}
        <div className="absolute inset-[12px] bg-black flex items-center justify-center overflow-hidden rounded-[32px] relative">
          {/* Boot flash effect */}
          <div
            className={`absolute inset-0 bg-white/80 z-10 transition-opacity duration-1000 pointer-events-none rounded-[32px]`}
            style={{
              opacity: glow ? 0 : 0.4,
            }}
          />
          
          <video
            src={logoVideo}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-contain transition-all duration-[2200ms] ease-out relative z-5"
            style={{
              transform: fadeOut ? "scale(1.08)" : "scale(1)",
              filter: fadeOut ? "blur(8px)" : "brightness(1)",
            }}
          />

          {/* Scanlines effect */}
          {scanlines && (
            <div
              className="absolute inset-0 pointer-events-none z-20 rounded-[32px]"
              style={{
                backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 2px)",
                animation: "scanlines 0.1s linear infinite",
              }}
            />
          )}

          {/* Glow overlay */}
          <div
            className={`absolute inset-0 pointer-events-none z-10 transition-opacity duration-1000 rounded-[32px]`}
            style={{
              background: `radial-gradient(circle at center, ${glowColor}, transparent 70%)`,
              opacity: glow ? 0.4 : 0,
            }}
          />
        </div>

        {/* Device bezel glow */}
        <div
          className="absolute -inset-1 pointer-events-none rounded-[40px] transition-opacity duration-1000"
          style={{
            background: `radial-gradient(circle at center, ${shadowColor}, transparent 80%)`,
            opacity: glow ? 0.6 : 0.3,
          }}
        />

        {/* Boot overlay text */}
        <div className="absolute bottom-10 w-full flex flex-col items-center z-30">
          <p
            className={`font-mono text-sm sm:text-base tracking-widest uppercase transition-all duration-700 font-bold ${
              textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
            }`}
            style={{
              color: mainColor,
              textShadow: `0 0 16px ${glowColor}, 0 0 8px ${glowColor}40`,
              letterSpacing: "2px",
            }}
          >
            {bootText}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-3 tracking-widest font-medium">
            {t('splash.systemVersion')}
          </p>
          <p className="text-[9px] text-gray-400 mt-1">
            {isPoweringOn ? t('splash.allOperational') : t('splash.systemsOffline')}
          </p>
        </div>
      </div>

      {/* Animated background particles */}
      {particlePositions.map((particle, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: "4px",
            height: "4px",
            background: mainColor,
            opacity: 0.2,
            animation: `float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
            boxShadow: `0 0 8px ${mainColor}40`,
          }}
        />
      ))}
    </div>
    </>
  );
}
